// src/components/Admin/ClientList.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  Plus,
  ExternalLink,
  Map,
  Users as UsersIcon,
  Pencil,
  Trash2,
  RefreshCw,
  Search,
} from "lucide-react";
import { listClients, deleteClient } from "../../services/adminService";
import StatusBadge from "./shared/StatusBadge";
import ConfirmDialog from "./shared/ConfirmDialog";

const ClientList = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listClients();
      setClients(data);
    } catch (err) {
      setError(err.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteClient(deleteTarget.id, true);
      setClients((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setError(err.message || "Failed to delete client");
    } finally {
      setDeleting(false);
    }
  };

  // Filter clients by search
  const filteredClients = clients.filter((client) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const name = client.config?.client?.name?.toLowerCase() || "";
    const slug = client.id?.toLowerCase() || "";
    const project = client.config?.client?.projectName?.toLowerCase() || "";
    return name.includes(q) || slug.includes(q) || project.includes(q);
  });

  // Stats
  const activeCount = clients.filter((c) => c.status === "active").length;
  const totalMaps = clients.reduce(
    (sum, c) => sum + (c.config?.maps?.length || 0),
    0,
  );

  return (
    <div>
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage client sites and configurations
          </p>
        </div>
        <Link
          to="/admin/clients/new"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary text-white text-sm font-medium rounded-lg hover:bg-secondary-dark transition shadow-sm self-start sm:self-auto"
        >
          <Plus size={18} />
          New Client
        </Link>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">
            {clients.length}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">Total Clients</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-emerald-600">
            {activeCount}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">Active</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{totalMaps}</div>
          <div className="text-xs text-gray-500 mt-0.5">Total Maps</div>
        </div>
      </div>

      {/* Search + refresh */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
          />
        </div>
        <button
          onClick={fetchClients}
          disabled={loading}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button
            onClick={fetchClients}
            className="ml-2 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && clients.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <RefreshCw
            size={24}
            className="mx-auto text-gray-400 animate-spin mb-3"
          />
          <p className="text-sm text-gray-500">Loading clients...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && clients.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Building2 size={40} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">
            No clients yet
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Create your first client to get started.
          </p>
          <Link
            to="/admin/clients/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-white text-sm font-medium rounded-lg hover:bg-secondary-dark transition"
          >
            <Plus size={16} />
            Create Client
          </Link>
        </div>
      )}

      {/* Client list */}
      {!loading && filteredClients.length > 0 && (
        <div className="space-y-3">
          {filteredClients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onEdit={() => navigate(`/admin/clients/${client.id}`)}
              onDelete={() => setDeleteTarget(client)}
            />
          ))}
        </div>
      )}

      {/* No search results */}
      {!loading && clients.length > 0 && filteredClients.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Search size={24} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">
            No clients matching "{searchQuery}"
          </p>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={`Delete "${deleteTarget?.config?.client?.name || deleteTarget?.id}"?`}
        message={
          <>
            This will permanently delete the client configuration and all
            associated files (scored data, reports, etc.) from Firebase. This
            action cannot be undone.
          </>
        }
        confirmLabel="Delete Client"
        confirmStyle="danger"
      />
    </div>
  );
};

// ============================================
// CLIENT CARD SUB-COMPONENT
// ============================================

const ClientCard = ({ client, onEdit, onDelete }) => {
  const config = client.config || {};
  const clientInfo = config.client || {};
  const avatars = config.avatars || [];
  const maps = config.maps || [];
  const states = config.states || [];

  // Format date
  const updatedAt = client.updatedAt?.toDate
    ? client.updatedAt.toDate()
    : client.updatedAt instanceof Date
      ? client.updatedAt
      : null;

  const updatedLabel = updatedAt
    ? updatedAt.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition group">
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          {/* Left: info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5 mb-1">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {clientInfo.name || client.id}
              </h3>
              <StatusBadge status={client.status || "draft"} />
            </div>

            <p className="text-sm text-gray-500 mb-3">
              <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                /{client.id}
              </span>
              {clientInfo.projectName && (
                <span className="ml-2 hidden sm:inline">
                  — {clientInfo.projectName}
                </span>
              )}
            </p>

            {/* Meta tags */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
              {states.length > 0 && (
                <span>{states.map((s) => s.abbreviation).join(", ")}</span>
              )}
              <span className="flex items-center gap-1">
                <Map size={12} />
                {maps.length} {maps.length === 1 ? "map" : "maps"}
              </span>
              <span>
                {avatars.length} {avatars.length === 1 ? "avatar" : "avatars"}
              </span>
              {updatedLabel && (
                <span className="hidden sm:inline">Updated {updatedLabel}</span>
              )}
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* View site */}
            {client.status === "active" && (
              <a
                href={`/${client.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-secondary hover:bg-secondary/10 rounded-lg transition"
                title="View live site"
              >
                <ExternalLink size={16} />
              </a>
            )}

            {/* Edit */}
            <button
              onClick={onEdit}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
              title="Edit client"
            >
              <Pencil size={16} />
            </button>

            {/* Delete */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
              title="Delete client"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientList;
